<template>
  <div class="personinfo-page">
    <!-- 用户信息 -->
    <div class="card user-card">
      <div class="avatar">{{ displayInitial }}</div>
      <div class="user-info">
        <div class="user-name">{{ displayName }}</div>
        <div class="user-desc">{{ isLogin ? '已登录，可管理课本与上传内容' : '登录后可管理课本与上传内容' }}</div>
      </div>
      <div class="user-actions">
        <el-button v-if="!isLogin" type="primary" size="small" @click="$router.push('/login')">登录</el-button>
        <el-button v-else size="small" @click="logout">退出登录</el-button>
      </div>
    </div>

    <!-- 使用统计 -->
    <div class="card stats-card">
      <h3 class="section-title">使用统计</h3>
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-num">{{ stats.historyCount }}</div>
          <div class="stat-label">查过单词</div>
        </div>
        <div class="stat-item">
          <div class="stat-num">{{ stats.daysCount }}</div>
          <div class="stat-label">使用天数</div>
        </div>
      </div>
      <div class="recent-words" v-if="stats.historyWords.length">
        <span class="recent-label">最近查词：</span>
        <el-tag
          v-for="(w, i) in stats.historyWords"
          :key="i"
          size="small"
          class="recent-tag"
          @click="goWord(w)"
        >{{ w }}</el-tag>
        <el-button type="text" size="mini" @click="clearHistory">清空</el-button>
      </div>
    </div>

    <!-- 我的课本 -->
    <div class="card book-card">
      <div class="book-header" @click="toggleCollapse">
        <h3 class="section-title">我的课本</h3>
        <i :class="bookCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'" />
      </div>

      <el-collapse-transition>
        <div v-show="!bookCollapsed">
          <div class="book-selects">
            <el-select v-model="bookSel.bookVersion" placeholder="版本" clearable size="small" @change="(v) => onBookChange('bookVersion', v)">
              <el-option v-for="v in bookRanges.bookVersion" :key="v" :label="v" :value="v" />
            </el-select>
            <el-select v-model="bookSel.stage" placeholder="阶段" clearable size="small" @change="(v) => onBookChange('stage', v)">
              <el-option v-for="v in bookRanges.stage" :key="v" :label="v" :value="v" />
            </el-select>
            <el-select v-model="bookSel.grade" placeholder="年级" clearable size="small" @change="(v) => onBookChange('grade', v)">
              <el-option v-for="v in bookRanges.grade" :key="v" :label="v" :value="v" />
            </el-select>
            <el-select v-model="bookSel.term" placeholder="学期" clearable size="small" @change="(v) => onBookChange('term', v)">
              <el-option v-for="v in bookRanges.term" :key="v" :label="v" :value="v" />
            </el-select>
            <el-select v-model="bookSel.unit" placeholder="单元" clearable size="small" @change="(v) => onBookChange('unit', v)">
              <el-option v-for="v in bookRanges.unit" :key="v" :label="v" :value="v" />
            </el-select>
          </div>

          <p class="book-tip" v-if="bookTip">{{ bookTip }}</p>

          <!-- 单元单词列表 -->
          <el-table :data="wordList" v-loading="wLoading" stripe size="small" style="margin-top: 12px">
            <el-table-column prop="word" label="单词" width="140" />
            <el-table-column prop="phonetic" label="音标" width="160" />
            <el-table-column prop="meaning" label="释义" min-width="200" show-overflow-tooltip />
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="text" @click="goWord(scope.row.word)">查词</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-wrap" v-if="wTotal > 0">
            <el-pagination
              background
              small
              layout="total, prev, pager, next"
              :total="wTotal"
              :page-size="20"
              :current-page="wPageNum"
              @current-change="onPageChange"
            />
          </div>
        </div>
      </el-collapse-transition>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

const FOUR_FIELDS = ['bookVersion', 'stage', 'grade', 'term']
const ALL_FIELDS = ['bookVersion', 'stage', 'grade', 'term', 'unit']
const DEFAULT_SEL4 = { bookVersion: '外研版三起点(2024)', stage: '小学', grade: '三年级', term: '上册' }
const DEFAULT_UNIT = 'WEL'

function parseRawSel(str) {
  const p = (str || '').split('|').map((s) => s.trim())
  return {
    four: { bookVersion: p[0] || '', stage: p[1] || '', grade: p[2] || '', term: p[3] || '' },
    unit: p[4] || ''
  }
}
function joinFour(four) {
  return FOUR_FIELDS.map((k) => (four && four[k]) || '').filter(Boolean).join('|')
}
function isFourComplete(four) {
  return FOUR_FIELDS.every((k) => !!(four && four[k]))
}
function isFourEmpty(four) {
  return FOUR_FIELDS.every((k) => !(four && four[k]))
}
function pickDefaultUnit(range, hint) {
  const arr = range || []
  if (!arr.length) return ''
  if (hint && arr.indexOf(hint) > -1) return hint
  if (arr.indexOf(DEFAULT_UNIT) > -1) return DEFAULT_UNIT
  return arr[0]
}

export default {
  name: 'PersonInfo',
  data() {
    return {
      bookCollapsed: localStorage.getItem('bookCollapsed') === '1',
      bookSel: { bookVersion: '', stage: '', grade: '', term: '', unit: '' },
      bookRanges: { bookVersion: [], stage: [], grade: [], term: [], unit: [] },
      bookTip: '',
      wordList: [],
      wLoading: false,
      wTotal: 0,
      wPageNum: 1,
      stats: {
        historyCount: 0,
        historyWords: [],
        daysCount: 0
      }
    }
  },
  computed: {
    isLogin() {
      return this.$store.getters.isLogin
    },
    displayName() {
      const info = this.$store.state.teacherInfo
      if (info && (info.username || info.name || info.nickname)) {
        return info.username || info.name || info.nickname
      }
      const token = this.$store.state.token
      if (this.isLogin && token) return '老师 ' + token.slice(-6)
      return '点击登录'
    },
    displayInitial() {
      if (this.isLogin && this.displayName !== '点击登录') {
        return this.displayName.charAt(0).toUpperCase()
      }
      return '👤'
    }
  },
  mounted() {
    this.syncStats()
    this.ensureBook()
  },
  methods: {
    syncStats() {
      let history = []
      try {
        history = JSON.parse(localStorage.getItem('searchHistory') || '[]')
      } catch (e) {
        history = []
      }
      let days = 0
      const firstUseAt = localStorage.getItem('firstUseAt')
      if (firstUseAt) {
        days = Math.max(1, Math.ceil((Date.now() - Number(firstUseAt)) / 86400000))
      }
      this.stats = {
        historyCount: history.length,
        historyWords: history.slice(0, 5),
        daysCount: days
      }
    },

    clearHistory() {
      localStorage.removeItem('searchHistory')
      this.syncStats()
      this.$message.success('已清空')
    },

    toggleCollapse() {
      this.bookCollapsed = !this.bookCollapsed
      localStorage.setItem('bookCollapsed', this.bookCollapsed ? '1' : '0')
    },

    ensureBook() {
      const raw = (this.$store.state.selectBook || '').trim()
      if (this._appliedRaw === raw) return
      this.applyBook(raw)
    },

    applyBook(raw) {
      this._appliedRaw = raw
      const parsed = parseRawSel(raw)
      let four = parsed.four
      if (isFourEmpty(four)) four = { ...DEFAULT_SEL4 }
      this._hintUnit = parsed.unit
      this.refreshAll(four, true)
    },

    async refreshAll(four) {
      const qs = FOUR_FIELDS
        .map((k) => {
          const v = (four[k] || '').trim()
          return v ? `${k}=${encodeURIComponent(v)}` : ''
        })
        .filter(Boolean)
        .join('&')
      let data = null
      try {
        const res = await request.get(`/bookWord/filterOptions${qs ? '?' + qs : ''}`)
        if (res.code === 200 && res.data) data = res.data
      } catch (e) {
        // 拦截器已提示
      }
      if (!data) return

      this.bookRanges = {
        bookVersion: data.bookVersion || [],
        stage: data.stage || [],
        grade: data.grade || [],
        term: data.term || [],
        unit: data.unit || []
      }

      let unit = this.bookSel.unit || ''
      if (isFourComplete(four)) {
        const arr = this.bookRanges.unit
        if (arr.indexOf(unit) === -1) {
          unit = pickDefaultUnit(arr, this._hintUnit)
          this._hintUnit = ''
        }
      } else {
        unit = ''
      }

      this.bookSel = {
        bookVersion: four.bookVersion || '',
        stage: four.stage || '',
        grade: four.grade || '',
        term: four.term || '',
        unit
      }
      this.bookTip = this.bookTipOf(this.bookSel)

      if (unit) this.loadWords(true, this.bookSel)
      else this.resetWordList()
    },

    bookTipOf(sel) {
      if (!sel || !sel.unit) {
        return isFourComplete(sel) ? '请在上方选择一个单元，下方展示该单元单词' : '先把 版本/阶段/年级/学期 选齐，即可选择单元'
      }
      return ''
    },

    onBookChange(field, value) {
      const cur = { ...this.bookSel }
      const pos = ALL_FIELDS.indexOf(field)
      if (pos < 0) return
      cur[field] = value || ''
      for (let i = pos + 1; i < ALL_FIELDS.length; i++) cur[ALL_FIELDS[i]] = ''
      this.bookSel = cur

      if (field === 'unit') {
        if (cur.unit) this.loadWords(true, cur)
        else this.resetWordList()
        return
      }
      this.refreshAll(cur).then(() => this.persistFour(cur))
    },

    persistFour(cur) {
      if (!isFourComplete(cur)) return
      const raw = joinFour(cur)
      this.$store.dispatch('setSelectBook', raw)
      this._appliedRaw = raw
      if (this.isLogin) {
        request.post('/api/teacher/selectBook', { selectBook: raw }).catch(() => {})
      }
    },

    async loadWords(reset, sel) {
      const b = sel || this.bookSel
      if (!isFourComplete(b) || !b.unit) {
        this.resetWordList()
        return
      }
      if (!reset && this.wLoading) return
      const pageNum = reset ? 1 : this.wPageNum
      this.wLoading = true
      try {
        const q = []
        ALL_FIELDS.forEach((k) => {
          const v = (b[k] || '').trim()
          if (v) q.push(`${k}=${encodeURIComponent(v)}`)
        })
        q.push(`pageNum=${pageNum}`, 'pageSize=20')
        const res = await request.get(`/bookWord/list?${q.join('&')}`)
        if (res.code === 200 && res.data) {
          this.wordList = res.data.records || []
          this.wTotal = res.data.total || 0
          this.wPageNum = res.data.current || 1
        }
      } catch (e) {
        // 拦截器已提示
      } finally {
        this.wLoading = false
      }
    },

    resetWordList() {
      this.wordList = []
      this.wTotal = 0
      this.wPageNum = 1
      this.bookTip = this.bookTipOf(this.bookSel)
    },

    onPageChange(page) {
      this.wPageNum = page
      this.loadWords(false)
    },

    goWord(word) {
      if (!word) return
      this.$store.dispatch('setWord', word)
      this.$router.push('/wordinfo')
    },

    async logout() {
      try {
        await request.post('/api/teacher/logout')
      } catch (e) {
        // 忽略登出接口异常
      }
      this.$store.dispatch('clearLogin')
      this.$message.success('已退出登录')
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.user-card {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #a29bfe, #6c5ce7);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
}
.user-info {
  flex: 1;
  margin-left: 16px;
}
.user-name {
  font-size: 18px;
  font-weight: 700;
}
.user-desc {
  margin-top: 4px;
  color: #909399;
  font-size: 13px;
}

.section-title {
  margin: 0 0 12px;
  font-size: 16px;
  color: #303133;
}
.stats-card {
  margin-bottom: 16px;
}
.stats-grid {
  display: flex;
  gap: 40px;
}
.stat-item {
  text-align: center;
}
.stat-num {
  font-size: 26px;
  font-weight: 800;
  color: #409eff;
}
.stat-label {
  margin-top: 4px;
  color: #909399;
  font-size: 13px;
}
.recent-words {
  margin-top: 16px;
}
.recent-label {
  color: #606266;
  font-size: 13px;
}
.recent-tag {
  margin-right: 8px;
  cursor: pointer;
}

.book-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}
.book-selects {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.book-selects .el-select {
  width: 170px;
}
.book-tip {
  margin: 10px 0 0;
  color: #e6a23c;
  font-size: 13px;
}
.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
